/**
 * Wallet-state cleanup utilities.
 *
 * Call `clearWalletState(address)` on logout to prevent stale subscription
 * status, yield data, and other wallet-scoped state from leaking to the next
 * wallet that connects in the same browser session.
 *
 * ── What gets cleared ──────────────────────────────────────────────────────
 * sovereign_sub_*              subscription state (plan, expiry, txHash)
 * sovereign_wallets_*          additional wallet list
 * sovereign_team_*             team member list
 * sovereign_yield_snaps_*      yield snapshot history
 * sovereign_measured_yield_*   computed measured-yield result
 * sovereign_wizard_dismissed_* yield-wizard dismiss marker
 * sovereign_joined_*           rewards-hub join timestamp
 * sovereign_sponsor_choice_*   onboarding sponsor selection
 * sovereign_verified_*         sLGNS verification cache
 * sovereign_*_migrated_*       one-time migration markers (idempotent to re-run)
 *
 * ── What is kept ───────────────────────────────────────────────────────────
 * privacy_mode          display preference — device-level, not wallet-specific
 * sovereign_referral_*  referral link — survives across wallets by design
 * protocol_metrics_*    protocol-level chart data — not personal
 * lgns-suite:*          calculator input state — device-level preference
 */

/** All localStorage key prefixes that are scoped to a single wallet address. */
const WALLET_SCOPED_PREFIXES = [
  'sovereign_sub_',
  'sovereign_wallets_',
  'sovereign_team_',
  'sovereign_yield_snaps_',
  'sovereign_measured_yield_',
  'sovereign_wizard_dismissed_',
  'sovereign_joined_',
  'sovereign_sponsor_choice_',
  'sovereign_verified_',
  // Migration markers: re-running the migration on next login is idempotent
  'sovereign_wallets_migrated_',
  'sovereign_team_migrated_',
  'sovereign_yield_migrated_',
] as const;

/**
 * Remove all wallet-scoped localStorage entries for the given address.
 * Safe to call even if localStorage is unavailable (private browsing, etc.).
 */
export function clearWalletState(address: string): void {
  const addr = address.toLowerCase();
  try {
    for (const prefix of WALLET_SCOPED_PREFIXES) {
      localStorage.removeItem(`${prefix}${addr}`);
    }
  } catch {
    // localStorage may be blocked in some private-browsing contexts — ignore
  }
}
